import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { useAuth } from './AuthContext';

export interface FoodEntry {
  id: string;
  date: string;
  foods: string[];
  imageUrl: string;
  nutritionInfo?: {
    calories: number;
    protein: number;
    carbs: number;
    fiber: number;
  };
  iaJson?: any; // JSON original de la IA
}

interface FoodHistoryContextType {
  history: FoodEntry[];
  addEntry: (entry: Omit<FoodEntry, 'id'>) => void;
  removeEntry: (id: string) => void;
  updateEntry: (id: string, entry: Partial<FoodEntry>) => void;
  filterByDate: (startDate: Date, endDate: Date) => FoodEntry[];
  searchByFood: (query: string) => FoodEntry[];
  getStats: () => {
    totalEntries: number;
    uniqueFoods: string[];
    averageFoodsPerDay: number;
    nutritionStats: {
      totalCalories: number;
      averageCalories: number;
      totalProtein: number;
      averageProtein: number;
      totalCarbs: number;
      averageCarbs: number;
      totalFiber: number;
      averageFiber: number;
    };
  };
  loading: boolean;
  error: string | null;
  refreshHistory: () => Promise<void>;
}

const FoodHistoryContext = createContext<FoodHistoryContextType | undefined>(undefined);

export function FoodHistoryProvider({ children }: { children: React.ReactNode }) {
  const [history, setHistory] = useState<FoodEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  // Función para cargar el historial desde el servidor
  const loadHistory = useCallback(async () => {
    if (!user?.id) return;

    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`https://greenbalance.poropo.es/api/history?user_id=${user.id}`);
      if (!response.ok) {
        throw new Error('Error al cargar el historial');
      }
      
      const data = await response.json();
      
      // Transformar los datos del servidor al formato del frontend
      const transformedData: FoodEntry[] = data.map((entry: any) => ({
        id: entry.id,
        date: entry.date,
        foods: entry.foods || [],
        imageUrl: entry.image_url || '',
        nutritionInfo: entry.nutrition_info ? {
          calories: entry.nutrition_info.calorias || entry.nutrition_info.calories || 0,
          protein: entry.nutrition_info.proteinas || entry.nutrition_info.protein || 0,
          carbs: entry.nutrition_info.carbohidratos || entry.nutrition_info.carbs || 0,
          fiber: entry.nutrition_info.fibra || entry.nutrition_info.fiber || 0,
        } : undefined,
        iaJson: entry.ia_json || null
      }));
      
      setHistory(transformedData);
    } catch (err) {
      console.error('Error cargando historial:', err);
      setError(err instanceof Error ? err.message : 'Error desconocido');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  // Función para refrescar el historial
  const refreshHistory = useCallback(async () => {
    await loadHistory();
  }, [loadHistory]);

  // Cargar historial cuando el usuario cambie
  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const addEntry = useCallback((entry: Omit<FoodEntry, 'id'>) => {
    const newEntry: FoodEntry = {
      ...entry,
      id: Date.now().toString(),
    };
    setHistory(prev => [newEntry, ...prev]); // Añadir al principio
  }, []);

  const removeEntry = useCallback((id: string) => {
    setHistory(prev => prev.filter(entry => entry.id !== id));
  }, []);

  const updateEntry = useCallback((id: string, updatedEntry: Partial<FoodEntry>) => {
    setHistory(prev => prev.map(entry => 
      entry.id === id ? { ...entry, ...updatedEntry } : entry
    ));
  }, []);

  const filterByDate = useCallback((startDate: Date, endDate: Date) => {
    return history.filter(entry => {
      const entryDate = new Date(entry.date);
      return entryDate >= startDate && entryDate <= endDate;
    });
  }, [history]);

  const searchByFood = useCallback((query: string) => {
    const searchQuery = query.toLowerCase();
    return history.filter(entry =>
      entry.foods.some(food => food.toLowerCase().includes(searchQuery))
    );
  }, [history]);

  const getStats = useCallback(() => {
    const uniqueFoods = Array.from(new Set(history.flatMap(entry => entry.foods)));
    const totalEntries = history.length;
    const averageFoodsPerDay = totalEntries > 0 
      ? history.reduce((acc, entry) => acc + entry.foods.length, 0) / totalEntries 
      : 0;

    const nutritionStats = history.reduce((acc, entry) => {
      if (entry.nutritionInfo) {
        acc.totalCalories += entry.nutritionInfo.calories;
        acc.totalProtein += entry.nutritionInfo.protein;
        acc.totalCarbs += entry.nutritionInfo.carbs;
        acc.totalFiber += entry.nutritionInfo.fiber;
      }
      return acc;
    }, {
      totalCalories: 0,
      totalProtein: 0,
      totalCarbs: 0,
      totalFiber: 0,
    });

    return {
      totalEntries,
      uniqueFoods,
      averageFoodsPerDay,
      nutritionStats: {
        ...nutritionStats,
        averageCalories: totalEntries > 0 ? nutritionStats.totalCalories / totalEntries : 0,
        averageProtein: totalEntries > 0 ? nutritionStats.totalProtein / totalEntries : 0,
        averageCarbs: totalEntries > 0 ? nutritionStats.totalCarbs / totalEntries : 0,
        averageFiber: totalEntries > 0 ? nutritionStats.totalFiber / totalEntries : 0,
      },
    };
  }, [history]);

  const value = useMemo(() => ({
    history,
    addEntry,
    removeEntry,
    updateEntry,
    filterByDate,
    searchByFood,
    getStats,
    loading,
    error,
    refreshHistory,
  }), [history, addEntry, removeEntry, updateEntry, filterByDate, searchByFood, getStats, loading, error, refreshHistory]);

  return (
    <FoodHistoryContext.Provider value={value}>
      {children}
    </FoodHistoryContext.Provider>
  );
}

export function useFoodHistory() {
  const context = useContext(FoodHistoryContext);
  if (context === undefined) {
    throw new Error('useFoodHistory must be used within a FoodHistoryProvider');
  }
  return context;
} 